import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import L from "leaflet";
import { Navigation, Store, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOW_EVERY_MS = 45000;
const CARD_LIFE_MS = 12000;

const radarPing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = radarPing._ctx || (radarPing._ctx = new Ctx());
    if (ctx.state === "suspended") { ctx.resume().catch(() => {}); return; }
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.28);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.5);
  } catch { /* audio blocked — stay silent */ }
};

export const SpotlightFlyer = ({ mapRef, onOpen }) => {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const pool = useRef([]);
  const markerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    let hideT = null;
    const refill = async () => {
      const m = mapRef.current;
      if (!m) return;
      const c = m.getCenter();
      try {
        const r = await axios.get(`${API}/spotlights`, { params: { lat: c.lat, lng: c.lng } });
        if (alive) pool.current = r.data.spotlights || [];
      } catch { /* quiet */ }
    };
    const showNext = () => {
      if (!alive || !pool.current.length) return;
      setCard(pool.current.shift());
      radarPing();
      hideT = setTimeout(() => alive && setCard(null), CARD_LIFE_MS);
    };
    refill().then(() => setTimeout(showNext, 12000));
    const refillT = setInterval(refill, 5 * 60000);
    const showT = setInterval(showNext, SHOW_EVERY_MS);
    return () => {
      alive = false;
      clearInterval(refillT); clearInterval(showT); if (hideT) clearTimeout(hideT);
      if (markerRef.current && mapRef.current) mapRef.current.removeLayer(markerRef.current);
    };
  }, [mapRef]);

  const takeMeThere = () => {
    const m = mapRef.current;
    if (!card || card.lat == null) {
      toast("That spotlight has no map pin yet 📍");
      setCard(null);
      return;
    }
    if (m) {
      m.flyTo([card.lat, card.lng], 16, { duration: 1.2 });
      if (markerRef.current) m.removeLayer(markerRef.current);
      markerRef.current = L.circleMarker([card.lat, card.lng], { radius: 12, color: "#f59e0b", weight: 3, fillColor: "#fbbf24", fillOpacity: 0.45 })
        .addTo(m).bindTooltip(`✨ ${card.name}`, { permanent: true, direction: "top", offset: [0, -12] });
      setTimeout(() => { if (markerRef.current && mapRef.current) { mapRef.current.removeLayer(markerRef.current); markerRef.current = null; } }, 20000);
    }
    // 🎯 Open the spotlight's deal panel so tapping actually lands somewhere.
    if (onOpen) {
      const c = card;
      setTimeout(() => onOpen({ id: c.business_id, name: c.name, lat: c.lat, lng: c.lng, category_id: c.category }, c), 900);
    }
    setCard(null);
  };

  return (
    <AnimatePresence>
      {card && (
        <motion.div
          initial={{ x: "110vw", opacity: 0.9 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "-110vw", opacity: 0 }}
          transition={{ type: "spring", stiffness: 60, damping: 16 }}
          className="fixed bottom-28 right-3 z-[1200] w-[290px] rounded-2xl overflow-hidden shadow-2xl shadow-amber-500/20"
          data-testid="spotlight-card"
        >
          <div className="bg-slate-900/95 backdrop-blur border border-amber-400/40 rounded-2xl p-3.5">
            <div className="flex items-start gap-2">
              <span className="w-8 h-8 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-amber-300" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-[9px] font-black tracking-[0.22em] uppercase text-amber-300">✨ POG SPOTLIGHT</p>
                <p className="text-[12.5px] text-slate-200 leading-snug mt-1">{card.blurb}</p>
              </div>
              <button data-testid="spotlight-close" onClick={() => setCard(null)} className="text-slate-500 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid="spotlight-route" onClick={takeMeThere}
                className="flex-1 h-9 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 text-xs font-heading font-black flex items-center justify-center gap-1.5">
                <Navigation className="w-3.5 h-3.5" /> TAKE ME THERE
              </button>
              <button data-testid="spotlight-claim" onClick={() => { setCard(null); navigate(`/business?claim=${encodeURIComponent(card.business_id)}&name=${encodeURIComponent(card.name)}&lat=${card.lat}&lng=${card.lng}&src=spotlight`); }}
                className="h-9 px-3 rounded-xl bg-slate-800 border border-slate-600 text-slate-300 text-[10px] font-bold flex items-center gap-1.5 hover:border-amber-400/50">
                <Store className="w-3.5 h-3.5" /> My biz? Claim FREE
              </button>
            </div>
            <p data-testid="spotlight-trial-line" className="text-[9px] text-slate-500 font-bold text-center mt-2">Claiming = 7-day FREE trial · then from $150/yr (or $30/mo)</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
